import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  MenuItem,
  IconButton,
  InputAdornment,
  Alert,
  Snackbar,
  CircularProgress,
  Autocomplete,
  Switch,
  FormControlLabel,
  Collapse
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import PageHeaderLayout from '../../components/Layout/PageHeaderLayout';
import { DeepLinkServices } from './DeepLinkServices';

const GenerateDeepLink = () => {
  const navigate = useNavigate();
  const menuDispatch = useDispatch();
  const { control, handleSubmit, watch, reset, setValue } = useForm({
    defaultValues: {
      linkType: 'sub-category',
      categoryId: '',
      subCategoryId: '',
      masterServiceId: '',
      childServiceId: '',
      providerId: '',
      bookingId: '',
      addUtm: false,
      utm_source: '',
      utm_medium: '',
      utm_campaign: '',
      utm_term: '',
      utm_content: '',
    }
  });

  const [generatedLink, setGeneratedLink] = useState('');
  const [showCopyAlert, setShowCopyAlert] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    menuDispatch({ type: 'menuSelect', payload: 'generate-deep-link' });
  }, [menuDispatch]);
  
  // Data states
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [masterServices, setMasterServices] = useState([]);
  const [childServices, setChildServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [bookings, setBookings] = useState([]);
  
  // Selected items for title generation
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedMasterService, setSelectedMasterService] = useState(null);
  const [selectedChildService, setSelectedChildService] = useState(null);
  
  const linkType = watch('linkType');
  const categoryId = watch('categoryId');
  const subCategoryId = watch('subCategoryId');
  const masterServiceId = watch('masterServiceId');

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch subcategories when category changes
  useEffect(() => {
    if (categoryId && (linkType === 'sub-category' || linkType === 'child-service')) {
      fetchSubCategories(categoryId);
    } else {
      setSubCategories([]);
      setValue('subCategoryId', '');
      setMasterServices([]);
      setValue('masterServiceId', '');
      setChildServices([]);
      setValue('childServiceId', '');
    }
  }, [categoryId, linkType]);

  // Fetch master services when subcategory changes
  useEffect(() => {
    if (subCategoryId && linkType === 'child-service') {
      console.log('Fetching master services for subcategory:', subCategoryId);
      fetchMasterServices(subCategoryId);
    }
  }, [subCategoryId]);

  // Fetch child services when master service changes
  useEffect(() => {
    if (masterServiceId && linkType === 'child-service') {
      console.log('Fetching child services for master service:', masterServiceId);
      fetchChildServices(masterServiceId);
    }
  }, [masterServiceId]);

  // Fetch providers/bookings when link type changes
  useEffect(() => {
    if (linkType === 'provider') {
      fetchProviders();
    } else if (linkType === 'booking') {
      fetchBookings();
    }
  }, [linkType]);

  // Reset form when link type changes
  useEffect(() => {
    setValue('categoryId', '');
    setValue('subCategoryId', '');
    setValue('masterServiceId', '');
    setValue('childServiceId', '');
    setValue('providerId', '');
    setValue('bookingId', '');
    // setValue('customPath', '');
    setGeneratedLink('');
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setSelectedMasterService(null);
    setSelectedChildService(null);
  }, [linkType]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await DeepLinkServices.getCategories();
      console.log('Categories API Response:', response);
      if (response.success === 1 && response.data) {
        setCategories(response.data);
        console.log('Categories loaded:', response.data.length);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubCategories = async (catId) => {
    setLoading(true);
    try {
      const response = await DeepLinkServices.getSubcategoriesByCategoryId(catId);
      console.log('SubCategories API Response:', response);
      if (response.success === 1 && response.data) {
        setSubCategories(response.data);
        console.log('SubCategories loaded:', response.data.length);
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterServices = async (subCatId) => {
    setLoading(true);
    try {
      const response = await DeepLinkServices.getServicesBySubcategory(subCatId);
      console.log('Master Services API Response:', response);
      if (response.success === 1 && response.data) {
        setMasterServices(response.data);
        console.log('Master Services loaded:', response.data.length);
        console.log('First Master Service:', response.data[0]);
        // Child services are already included in the response, we'll extract them when master service is selected
      }
    } catch (error) {
      console.error('Error fetching master services:', error);
      setMasterServices([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchChildServices = async (masterSvcId) => {
    // Child services are already included in the master services response
    // Extract them from the selected master service
    const selectedMaster = masterServices.find(m => m.masterServiceId == masterSvcId);
    console.log('Selected Master Service:', selectedMaster);
    
    if (selectedMaster && selectedMaster.childServices) {
      const childData = selectedMaster.childServices.map(child => ({
        id: child.childServiceId,
        name: child.title,
        title: child.title,
        shortTitle: child.shortTitle,
        description: child.description,
        ...child
      }));
      setChildServices(childData);
      console.log('Child Services extracted from master:', childData.length);
      console.log('First Child Service:', childData[0]);
    } else {
      console.warn('No child services found in selected master service');
      setChildServices([]);
    }
  };

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const response = await DeepLinkServices.getProviders({ limit: 100 });
      console.log('Providers API Response:', response);
      if (response.success === 1 && response.data) {
        // Handle different response structures
        const providersData = response.data.experts || 
                             response.data.providers || 
                             (Array.isArray(response.data) ? response.data : 
                             (response.data.data || response.data.list || response.data.rows || []));
        setProviders(providersData);
        console.log('Providers loaded:', providersData.length);
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
      setProviders([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await DeepLinkServices.getBookings({ limit: 100 });
      console.log('Bookings API Response:', response);
      if (response.success === 1 && response.data) {
        // Handle different response structures
        const bookingsData = response.data.bookings || 
                            (Array.isArray(response.data) ? response.data : 
                            (response.data.data || response.data.list || response.data.rows || []));
        setBookings(bookingsData);
        console.log('Bookings loaded:', bookingsData.length);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setBookings([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    // Use production URL in production, staging otherwise
    const baseUrl = process.env.REACT_APP_ENV_MODE === 'production' 
      ? 'https://cudel.in' 
      : 'https://staging.cudel.in';
    let link = '';

    // Build UTM query string (appended before title)
    const buildUtmParams = () => {
      if (!data.addUtm) return '';
      const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
      return utmKeys
        .filter(k => data[k]?.trim())
        .map(k => `${k}=${encodeURIComponent(data[k].trim())}`)
        .join('&');
    };

    const appendUtmAndTitle = (baseParams, title) => {
      const utm = buildUtmParams();
      let url = baseParams;
      // if (utm) url += `&${utm}`;
      // if (title) url += `&title=${encodeURIComponent(title)}`;

      let queryParams = [];
      if (utm) queryParams.push(utm);
      if (title) queryParams.push(`title=${encodeURIComponent(title)}`);
      if (queryParams.length > 0) {
        url += (url.includes('?') ? '&' : '?') + queryParams.join('&');
      }
      return url;
    };
    
    switch (data.linkType) {
      case 'sub-category':
        if (data.categoryId && data.subCategoryId) {
          const title = selectedCategory?.name || selectedCategory?.title || selectedCategory?.categoryName || 'Category';
          link = appendUtmAndTitle(
            // `${baseUrl}/sub-category?categoryId=${data.categoryId}&subCategoryId=${data.subCategoryId}`,
            `${baseUrl}/sc/${data.categoryId}/${data.subCategoryId}`,
            title
          );
        }
        break;
      
      case 'child-service':
        if (data.masterServiceId && data.childServiceId) {
          const title = selectedMasterService?.title || selectedMasterService?.name || selectedMasterService?.serviceName || 'Service';
          link = appendUtmAndTitle(
            // `${baseUrl}/child-service?masterServiceId=${data.masterServiceId}&childServiceId=${data.childServiceId}`,
            `${baseUrl}/cs/${data.masterServiceId}/${data.childServiceId}`,
            title
          );
        }
        break;
      
      case 'provider':
        if (data.providerId) {
          // link = appendUtmAndTitle(`${baseUrl}/provider?providerId=${data.providerId}`, '');
          link = appendUtmAndTitle(`${baseUrl}/p/${data.providerId}`, '');
        }
        break;
      
      case 'booking':
        if (data.bookingId) {
          // link = appendUtmAndTitle(`${baseUrl}/booking?bookingId=${data.bookingId}`, '');
          link = appendUtmAndTitle(`${baseUrl}/b/${data.bookingId}`, '');
        }
        break;
      
      default:
        link = '';
    }

    if (!link) return;

    setLoading(true);
    try {
      const response = await DeepLinkServices.storeDeepUrl(link);
      if (response.success === 1) {
        setGeneratedLink(link);
      }
    } catch (error) {
      console.error('Error storing deep link:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    setShowCopyAlert(true);
  };

  const handleReset = () => {
    reset();
    setGeneratedLink('');
  };

  return (
    <>
      <PageHeaderLayout headerTitle="Generate Deep Link">
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate('/marketing/generate-deep-link')}
        >
          Show List
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={handleReset}
          sx={{ fontSize: { xs: '.5rem', sm: '.75rem' } }}
        >
          Reset Form
        </Button>
      </PageHeaderLayout>

      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Grid container spacing={3}>
          {/* Form Section */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Deep Link Configuration
                </Typography>
                
                <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
                  <Controller
                    name="linkType"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="Link Type"
                        fullWidth
                        sx={{ mb: 2 }}
                      >
                        <MenuItem value="sub-category">Sub-Category</MenuItem>
                        <MenuItem value="child-service">Child Service</MenuItem>
                        <MenuItem value="provider">Service Expert</MenuItem>
                        <MenuItem value="booking">Booking</MenuItem>
                       
                      </TextField>
                    )}
                  />

                  {/* Sub-Category Link Fields */}
                  {linkType === 'sub-category' && (
                    <>
                      <Controller
                        name="categoryId"
                        control={control}
                        rules={{ required: 'Category is required' }}
                        render={({ field, fieldState: { error } }) => (
                          <Autocomplete
                            {...field}
                            options={categories}
                            getOptionLabel={(option) => option.name || option.title || option.categoryName || `ID: ${option.id}`}
                            isOptionEqualToValue={(option, value) => option.id == value?.id || option.id == value}
                            loading={loading}
                            onChange={(_, value) => {
                              field.onChange(value?.id || '');
                              setSelectedCategory(value);
                              console.log('Selected Category:', value);
                            }}
                            value={categories.find(c => c.id == field.value) || null}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Category"
                                error={!!error}
                                helperText={error?.message}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loading ? <CircularProgress size={20} /> : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            sx={{ mb: 2 }}
                          />
                        )}
                      />

                      <Controller
                        name="subCategoryId"
                        control={control}
                        rules={{ required: 'Sub-Category is required' }}
                        render={({ field, fieldState: { error } }) => (
                          <Autocomplete
                            {...field}
                            options={subCategories}
                            getOptionLabel={(option) => option.name || option.title || option.subCategoryName || `ID: ${option.id}`}
                            isOptionEqualToValue={(option, value) => option.id == value?.id || option.id == value}
                            loading={loading}
                            disabled={!categoryId}
                            onChange={(_, value) => {
                              field.onChange(value?.id || '');
                              setSelectedSubCategory(value);
                              console.log('Selected SubCategory:', value);
                            }}
                            value={subCategories.find(s => s.id == field.value) || null}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Sub-Category"
                                error={!!error}
                                helperText={error?.message || 'Select category first'}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loading ? <CircularProgress size={20} /> : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            sx={{ mb: 2 }}
                          />
                        )}
                      />
                    </>
                  )}

                  {/* Child Service Link Fields */}
                  {linkType === 'child-service' && (
                    <>
                      <Controller
                        name="categoryId"
                        control={control}
                        rules={{ required: 'Category is required' }}
                        render={({ field, fieldState: { error } }) => (
                          <Autocomplete
                            {...field}
                            options={categories}
                            getOptionLabel={(option) => option.name || option.title || option.categoryName || `ID: ${option.id}`}
                            isOptionEqualToValue={(option, value) => option.id == value?.id || option.id == value}
                            loading={loading}
                            onChange={(_, value) => {
                              field.onChange(value?.id || '');
                              setSelectedCategory(value);
                              console.log('Selected Category:', value);
                            }}
                            value={categories.find(c => c.id == field.value) || null}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Category"
                                error={!!error}
                                helperText={error?.message}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loading ? <CircularProgress size={20} /> : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            sx={{ mb: 2 }}
                          />
                        )}
                      />

                      <Controller
                        name="subCategoryId"
                        control={control}
                        rules={{ required: 'Sub-Category is required' }}
                        render={({ field, fieldState: { error } }) => (
                          <Autocomplete
                            {...field}
                            options={subCategories}
                            getOptionLabel={(option) => option.name || option.title || option.subCategoryName || `ID: ${option.id}`}
                            isOptionEqualToValue={(option, value) => option.id == value?.id || option.id == value}
                            loading={loading}
                            disabled={!categoryId}
                            onChange={(_, value) => {
                              field.onChange(value?.id || '');
                              setSelectedSubCategory(value);
                              console.log('Selected SubCategory:', value);
                            }}
                            value={subCategories.find(s => s.id == field.value) || null}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Sub-Category"
                                error={!!error}
                                helperText={error?.message || 'Select category first'}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loading ? <CircularProgress size={20} /> : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            sx={{ mb: 2 }}
                          />
                        )}
                      />

                      <Controller
                        name="masterServiceId"
                        control={control}
                        rules={{ required: 'Master Service is required' }}
                        render={({ field, fieldState: { error } }) => (
                          <Autocomplete
                            options={masterServices}
                            getOptionLabel={(option) => option.title || option.name || option.serviceName || `ID: ${option.masterServiceId}`}
                            isOptionEqualToValue={(option, value) => {
                              const result = option.masterServiceId == value?.masterServiceId || option.masterServiceId == value;
                              return result;
                            }}
                            loading={loading}
                            disabled={!subCategoryId}
                            onChange={(_, value) => {
                              console.log('Master Service onChange:', value);
                              const newId = value?.masterServiceId || '';
                              field.onChange(newId);
                              setSelectedMasterService(value);
                            }}
                            value={masterServices.find(m => m.masterServiceId == field.value) || null}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Master Service"
                                error={!!error}
                                helperText={error?.message || `Select sub-category first. Current value: ${field.value || 'none'}`}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loading ? <CircularProgress size={20} /> : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            sx={{ mb: 2 }}
                          />
                        )}
                      />

                      <Controller
                        name="childServiceId"
                        control={control}
                        rules={{ required: 'Child Service is required' }}
                        render={({ field, fieldState: { error } }) => (
                          <Autocomplete
                            options={childServices}
                            getOptionLabel={(option) => option.title || option.name || option.serviceName || `ID: ${option.id}`}
                            isOptionEqualToValue={(option, value) => {
                              const result = option.id == value?.id || option.id == value;
                              return result;
                            }}
                            loading={loading}
                            disabled={!masterServiceId}
                            noOptionsText={masterServiceId ? "No child services available" : "Select master service first"}
                            onChange={(_, value) => {
                              console.log('Child Service onChange:', value);
                              const newId = value?.id || '';
                              field.onChange(newId);
                              setSelectedChildService(value);
                            }}
                            value={childServices.find(c => c.id == field.value) || null}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Child Service"
                                error={!!error}
                                helperText={error?.message || `Select master service first. Options: ${childServices.length}. Current value: ${field.value || 'none'}`}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loading ? <CircularProgress size={20} /> : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            sx={{ mb: 2 }}
                          />
                        )}
                      />
                    </>
                  )}

                  {/* Provider Link Fields */}
                  {linkType === 'provider' && (
                    <Controller
                      name="providerId"
                      control={control}
                      rules={{ required: 'Service Expert is required' }}
                      render={({ field, fieldState: { error } }) => (
                        <Autocomplete
                          {...field}
                          options={providers}
                          getOptionLabel={(option) => {
                            const name = option.name || option.fullName || option.firstName || option.UserProfile?.firstName || 'Unknown';
                            const mobile = option.mobile || option.phone || option.mobileNumber || option.mobileNo || '';
                            const id = option.id || '';
                            return `${name}${mobile ? ' - ' + mobile : ''} (ID: ${id})`;
                          }}
                          isOptionEqualToValue={(option, value) => option.id == value?.id || option.id == value}
                          filterOptions={(options, { inputValue }) => {
                            if (!inputValue) return options;
                            const searchTerm = inputValue.toLowerCase();
                            return options.filter(option => {
                              const name = (option.name || option.fullName || option.firstName || option.UserProfile?.firstName || '').toLowerCase();
                              const mobile = (option.mobile || option.phone || option.mobileNumber || option.mobileNo || '').toLowerCase();
                              const id = String(option.id || '');
                              return name.includes(searchTerm) || mobile.includes(searchTerm) || id.includes(searchTerm);
                            });
                          }}
                          loading={loading}
                          onChange={(_, value) => {
                            field.onChange(value?.id || '');
                            console.log('Selected Provider:', value);
                          }}
                          value={providers.find(p => p.id == field.value) || null}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Service Expert (Search by Name)"
                              error={!!error}
                              helperText={error?.message || 'Search by expert name, mobile or ID'}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loading ? <CircularProgress size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          sx={{ mb: 2 }}
                        />
                      )}
                    />
                  )}

                  {/* Booking Link Fields */}
                  {linkType === 'booking' && (
                    <Controller
                      name="bookingId"
                      control={control}
                      rules={{ required: 'Booking ID is required' }}
                      render={({ field, fieldState: { error } }) => (
                        <Autocomplete
                          {...field}
                          freeSolo
                          options={bookings}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            return `Booking #${option.id} - ${option.customerName || 'N/A'}`;
                          }}
                          loading={loading}
                          onChange={(_, value) => {
                            if (typeof value === 'string') {
                              field.onChange(value);
                            } else {
                              field.onChange(value?.id || '');
                            }
                            console.log('Selected Booking:', value);
                          }}
                          onInputChange={(_, newValue) => {
                            if (newValue && !isNaN(newValue)) {
                              field.onChange(newValue);
                            }
                          }}
                          value={bookings.find(b => b.id == field.value) || field.value || null}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Booking ID"
                              error={!!error}
                              helperText={error?.message || 'Select from dropdown or enter booking ID manually'}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loading ? <CircularProgress size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          sx={{ mb: 2 }}
                        />
                      )}
                    />
                  )}

                  {/* UTM Parameters */}
                  <Box sx={{ mt: 2, mb: 1 }}>
                    <Controller
                      name="addUtm"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                              color="primary"
                            />
                          }
                          label="Add UTM Parameters"
                        />
                      )}
                    />
                  </Box>
                  <Collapse in={!!watch('addUtm')}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {[
                        { name: 'utm_source', label: 'UTM Source', placeholder: 'e.g. whatsapp' },
                        { name: 'utm_medium', label: 'UTM Medium', placeholder: 'e.g. notification' },
                        { name: 'utm_campaign', label: 'UTM Campaign', placeholder: 'e.g. summer_sale' },
                        { name: 'utm_term', label: 'UTM Term', placeholder: 'e.g. ac_repair' },
                        { name: 'utm_content', label: 'UTM Content', placeholder: 'e.g. banner_1' },
                      ].map(({ name, label, placeholder }) => (
                        <Grid item xs={12} sm={6} key={name}>
                          <Controller
                            name={name}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label={label}
                                fullWidth
                                size="small"
                                placeholder={placeholder}
                              />
                            )}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Collapse>

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    startIcon={<LinkIcon />}
                    sx={{ mt: 2 }}
                  >
                    Generate Deep Link
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Result Section */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Generated Deep Link
                </Typography>
                
                {generatedLink ? (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      value={generatedLink}
                      fullWidth
                      multiline
                      rows={3}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleCopyLink}
                              edge="end"
                              color="primary"
                            >
                              <ContentCopyIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 2 }}
                    />
                    
                    <Alert severity="success" sx={{ mt: 2 }}>
                      Deep link generated successfully! Click the copy icon to copy the link.
                    </Alert>

                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Usage Instructions:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • Use this link in notifications, emails, or SMS
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • The link will open the Cudel app directly to the specified content
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • Ensure the app is installed on the user's device
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ 
                    mt: 2, 
                    p: 4, 
                    textAlign: 'center',
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 1
                  }}>
                    <LinkIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      Fill in the form and click "Generate Deep Link" to create your link
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={showCopyAlert}
        autoHideDuration={2000}
        onClose={() => setShowCopyAlert(false)}
        message="Link copied to clipboard!"
      />
    </>
  );
};

export default GenerateDeepLink;
